import React, { useState, useEffect } from "react";
import {
  TextField,
  Container,
  Typography,
  Box,
  Paper,
  InputAdornment,
  CircularProgress,
  Grid,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Alert,
  Snackbar,
  Button,
} from "@mui/material";
import { Search, AddCircleOutline, FolderShared } from "@mui/icons-material";
import Sidebar from "../components/Sidebar";
import SnippetCard from "../components/SnippetCard";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logout } from "../redux/authSlice";

const MySnippets = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [snippets, setSnippets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const darkMode = useSelector((state) => state.darkMode.isDarkMode);
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: {
            main: darkMode ? "#90caf9" : "#1976d2",
          },
          background: {
            default: darkMode ? "#121212" : "#f5f5f5",
            paper: darkMode ? "#1e1e1e" : "#ffffff",
          },
        },
      }),
    [darkMode]
  );

  useEffect(() => {
    // Fetch snippets from backend
    const fetchSnippets = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_API_URL}/snippets/userSnippets`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          }
        );

        const data = await response.json();

        if (data.message === "Invalid token") {
          // Remove user from local storage
          dispatch(logout());
          return;
        }

        // console.log("data", data);
        setSnippets(data.snippets);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching snippets:", error);
        setError("Failed to load your snippets. Please try again later.");
        setLoading(false);
      }
    };

    fetchSnippets();
  }, [dispatch, user.token]);

  // Filter snippets based on search input
  const filteredSnippets = snippets.filter(
    (snippet) =>
      snippet.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      snippet.language.toLowerCase().includes(searchQuery.toLowerCase()) ||
      snippet.tags.some((tag) =>
        tag.toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  const handleDeleteSnippet = async (snippetId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/snippets/${snippetId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        // Remove the deleted snippet from state
        setSnippets(snippets.filter((snippet) => snippet._id !== snippetId));
        setNotification({
          open: true,
          message: data.message,
          severity: "success",
        });
      } else {
        setNotification({
          open: true,
          message: data.message || "Failed to delete snippet",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error deleting snippet:", error);
      setNotification({
        open: true,
        message: "Error deleting snippet. Please try again.",
        severity: "error",
      });
    }
  };

  const handleEditSnippet = (snippetId) => {
    navigate(`/edit-snippet/${snippetId}`);
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const containerStyles = {
    maxWidth: { sm: "sm", md: "md", lg: "lg", xl: "xl" },
    px: { xs: 2, sm: 3 },
    mx: "auto",
  };

  const headerStyles = {
    mb: { xs: 2, sm: 3 },
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    gap: { xs: 2, sm: 0 },
    justifyContent: "space-between",
    alignItems: { xs: "stretch", sm: "center" },
  };

  const searchBarStyles = {
    p: { xs: 1.5, sm: 2 },
    mb: { xs: 2, sm: 3 },
    borderRadius: 2,
    transition: "all 0.3s",
    "&:hover": {
      boxShadow: (theme) => theme.shadows[4],
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <Sidebar />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            transition: "all 0.3s",
            bgcolor: "background.default",
          }}
        >
          <Container sx={containerStyles}>
            <Box sx={headerStyles}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <FolderShared
                  sx={{ fontSize: 32, mr: 2, color: "primary.main" }}
                />
                <Typography variant="h4" component="h1" fontWeight="bold">
                  My Snippets
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddCircleOutline />}
                onClick={() => navigate("/add-snippet")}
              >
                Add New Snippet
              </Button>
            </Box>

            <Paper elevation={3} sx={searchBarStyles}>
              <TextField
                fullWidth
                placeholder="Search by Title, Language, Tags"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ bgcolor: "background.paper" }}
              />
            </Paper>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
                <Typography variant="h6" color="error">
                  {error}
                </Typography>
              </Box>
            ) : filteredSnippets.length > 0 ? (
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                {filteredSnippets.map((snippet) => (
                  <Grid item xs={12} sm={6} lg={4} key={snippet._id}>
                    <SnippetCard
                      snippet={snippet}
                      subHeaderTitle={"Created on"}
                      onDelete={handleDeleteSnippet}
                      onEdit={handleEditSnippet}
                      showActions={true}
                      sx={{
                        height: "100%",
                        transition: "transform 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                        },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  my: 10,
                }}
              >
                <Paper
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    p: 5,
                    borderRadius: 2,
                    maxWidth: 500,
                  }}
                >
                  <FolderShared
                    sx={{ fontSize: 80, mb: 2, color: "text.secondary" }}
                  />
                  <Typography variant="h5" gutterBottom fontWeight="medium">
                    No Snippets Found
                  </Typography>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    align="center"
                    paragraph
                  >
                    {searchQuery
                      ? "Try adjusting your search terms or clear the search"
                      : "You haven't created any snippets yet. Get started by adding your first code snippet!"}
                  </Typography>
                  {!searchQuery && (
                    <Button
                      variant="contained"
                      startIcon={<AddCircleOutline />}
                      onClick={() => navigate("/add-snippet")}
                      sx={{ mt: 2 }}
                    >
                      Create Your First Snippet
                    </Button>
                  )}
                </Paper>
              </Box>
            )}
          </Container>

          <Snackbar
            open={notification.open}
            autoHideDuration={5000}
            onClose={handleCloseNotification}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={handleCloseNotification}
              severity={notification.severity}
              sx={{ width: "100%" }}
              variant="filled"
            >
              {notification.message}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default MySnippets;
